import React from 'react'
import { navigate } from 'gatsby'
import { Icon, Tooltip } from '@chakra-ui/react'
import { Box, Flex, Text, TextProps, Spacer } from '@chakra-ui/react'
import { LuExternalLink } from 'react-icons/lu'

import {
    UserFieldsFragment,
    InternalChatFieldsFragment,
    InternalChatUserFieldsFragment,
    InternalChatUserPreviewFieldsFragment,
} from '~graphql/generated/graphql'
import { UserRoutes } from '~config/routes'
import { getUserFullName } from '~utils/helpers'
import UnreadIndicator from './unread-indicator'
import { useUserContext } from '~config/user-context'
import SharedLabel from '~components/shared/form/shared-label'
import SharedStack from '~components/shared/wrappers/shared-stack'
import SharedHelpText from '~components/shared/form/shared-help-text'
import { InternalChatUserAvatar } from '~components/team-chat/user-avatar'
import { formatHumanReadableDate } from '~components/chats/chat/messages-list/message-content/message-helpers'
import IconButtonWithTooltip from '~components/shared/icon-button-with-tooltip'
import { useOpenTeamChats } from '~components/team-chat/popovers/open-team-chats-provider'

interface Props {
    internalChatUser: InternalChatUserFieldsFragment
    chatId?: string
    isNested?: boolean
}

export default function TeamChatListItem({
    internalChatUser,
    chatId,
    isNested,
}: Props) {
    const { user: authUser } = useUserContext()
    const { openTeamChat } = useOpenTeamChats()
    const { internal_chat: chat, unread_message_count } = internalChatUser
    const { internal_chat_users: allUsers } = chat

    const isSelected = chatId ? chat.id === chatId : false
    const isUnread = unread_message_count ? unread_message_count > 0 : false
    const mostRecentUser = [...allUsers]
        .sort((a, b) => {
            return b.last_message_time - a.last_message_time
        })
        .at(0)
    const displayUsers = [...allUsers]
        .filter(user => user.user.id !== authUser?.id)
        .sort((a, b) => {
            return b.created_at - a.created_at
        })

    function handleChatSelected() {
        navigate(UserRoutes.TeamChatWithId(chat.id))
    }

    function handlePopOut(event: React.MouseEvent) {
        event.stopPropagation()
        openTeamChat(chat.id)
        if (isSelected) {
            navigate(UserRoutes.TeamChat)
        }
    }

    return (
        <SharedStack
            row
            role="group"
            py={1}
            px={2}
            pl={isNested ? 4 : 2}
            align="flex-start"
            spacing={2}
            rounded="xl"
            cursor="pointer"
            onClick={handleChatSelected}
            bg={isSelected ? 'brand.600' : 'transparent'}
            _hover={{ bg: isSelected ? 'brand.700' : 'gray.100' }}
        >
            <Box pt={0.5}>
                <Tooltip
                    placement="end"
                    hasArrow
                    isDisabled={
                        !mostRecentUser?.user ||
                        mostRecentUser.last_message_time === 0
                    }
                    label={
                        <Box>
                            <Text>
                                {mostRecentUser?.user &&
                                    (mostRecentUser?.user?.id === authUser?.id
                                        ? 'You'
                                        : getUserFullName(
                                              mostRecentUser?.user,
                                          ))}
                            </Text>
                            <Text>
                                Sent{' '}
                                {mostRecentUser?.last_message_time &&
                                    formatHumanReadableDate(
                                        mostRecentUser?.last_message_time,
                                    )}
                            </Text>
                        </Box>
                    }
                >
                    <Box>
                        <InternalChatUserAvatar user={mostRecentUser} />
                    </Box>
                </Tooltip>
            </Box>
            <Flex
                flex={1}
                flexDir="row"
                align="flex-start"
                w="full"
                justify="space-between"
            >
                <Box w="full">
                    <InternalChatTitle
                        chat={chat}
                        users={displayUsers}
                        isUnread={isUnread}
                        isSelected={isSelected}
                    />
                    <SharedHelpText
                        fontSize="xs"
                        noOfLines={1}
                        color={isSelected ? 'gray.100' : 'gray.500'}
                    >
                        {chat.last_message || 'No messages yet'}
                    </SharedHelpText>
                </Box>
                <Spacer />
                <SharedStack row fit spacing={1} align="center" pt={0.5}>
                    <IconButtonWithTooltip
                        icon={<Icon as={LuExternalLink} fontSize="xs" />}
                        label="Pop out chat"
                        size="xs"
                        variant="ghost"
                        rounded="full"
                        color={isSelected ? 'white' : 'gray.500'}
                        opacity={isSelected ? 1 : 0}
                        pointerEvents={isSelected ? 'auto' : 'none'}
                        _groupHover={{ opacity: 1, pointerEvents: 'auto' }}
                        _hover={{
                            bg: isSelected ? 'whiteAlpha.300' : 'gray.200',
                        }}
                        onClick={handlePopOut}
                    />
                    <UnreadIndicator unreadCounts={unread_message_count} />
                </SharedStack>
            </Flex>
        </SharedStack>
    )
}

export function InternalChatTitle({
    chat,
    users,
    isUnread,
    isSelected,
    labelOverrides,
}: {
    chat: InternalChatFieldsFragment
    users: InternalChatUserPreviewFieldsFragment[]
    isUnread?: boolean
    isSelected?: boolean
    labelOverrides?: Omit<TextProps, 'children'>
}) {
    const userPreviews = users.map(user => user.user)
    const userNames = userPreviews.map(user => getUserFullName(user)).join(', ')
    const userCount = userPreviews.length
    const remainingCount = userCount > 3 ? `+${userCount - 3}` : ''
    const title = chat?.title
        ? chat.title
        : `${userNames}${remainingCount ? ` ${remainingCount}` : ''}`

    return (
        <SharedLabel
            noOfLines={1}
            fontWeight={
                isUnread ? 'semibold' : isSelected ? 'medium' : 'normal'
            }
            color={isSelected ? 'white' : 'gray.900'}
            fontSize="13px"
            {...labelOverrides}
        >
            {title}
        </SharedLabel>
    )
}

export function getDefaultChatTitle(
    users: Pick<UserFieldsFragment, 'id' | 'first_name' | 'last_name'>[],
) {
    const userNames = users.map(user => getUserFullName(user)).join(', ')
    const userCount = users.length
    const remainingCount = userCount > 3 ? `+${userCount - 3}` : ''

    return userCount > 0
        ? `${userNames}${remainingCount ? ` ${remainingCount}` : ''}`
        : 'New Chat'
}
